import type {
  ProductQueryParams,
  ProductSearchParams,
  ProductStatsParams,
  AdminUserQueryParams,
  AdminProductQueryParams,
  AnnouncementQueryParams,
  AuditLogQueryParams,
  ViewPlatform,
} from './types';

/**
 * Query key factory for React Query.
 * Hierarchical structure enables targeted cache invalidation.
 */
export const queryKeys = {
  users: {
    all: ['users'] as const,
    me: () => [...queryKeys.users.all, 'me'] as const,
    deletionInfo: () => [...queryKeys.users.all, 'deletion-info'] as const,
  },
  households: {
    all: ['households'] as const,
    list: () => [...queryKeys.households.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.households.all, 'detail', id] as const,
    members: (householdId: string) =>
      [...queryKeys.households.all, householdId, 'members'] as const,
    invitations: (householdId: string) =>
      [...queryKeys.households.all, householdId, 'invitations'] as const,
  },
  invitations: {
    all: ['invitations'] as const,
    preview: (token: string) => [...queryKeys.invitations.all, 'preview', token] as const,
  },
  products: {
    all: ['products'] as const,
    list: (params?: ProductQueryParams) => [...queryKeys.products.all, 'list', params] as const,
    detail: (id: string) => [...queryKeys.products.all, 'detail', id] as const,
    barcode: (barcode: string) => [...queryKeys.products.all, 'barcode', barcode] as const,
  },
  storageLocations: {
    all: ['storageLocations'] as const,
    byHousehold: (householdId: string) => [...queryKeys.storageLocations.all, householdId] as const,
    list: (householdId: string) =>
      [...queryKeys.storageLocations.all, householdId, 'list'] as const,
    detail: (householdId: string, id: string) =>
      [...queryKeys.storageLocations.all, householdId, 'detail', id] as const,
  },
  items: {
    all: ['items'] as const,
    byHousehold: (householdId: string) => [...queryKeys.items.all, householdId] as const,
    list: (householdId: string, params?: Record<string, unknown>) =>
      [...queryKeys.items.all, householdId, 'list', params] as const,
    detail: (householdId: string, id: string) =>
      [...queryKeys.items.all, householdId, 'detail', id] as const,
    stats: (householdId: string) => [...queryKeys.items.all, householdId, 'stats'] as const,
    expiringSoon: (householdId: string, days?: number) =>
      [...queryKeys.items.all, householdId, 'expiring-soon', days ?? 'default'] as const,
    expiringSoonAll: (householdId: string) =>
      [...queryKeys.items.all, householdId, 'expiring-soon'] as const,
    instances: (householdId: string, itemId: string) =>
      [...queryKeys.items.all, householdId, itemId, 'instances'] as const,
    searchProducts: (householdId: string, params: ProductSearchParams) =>
      [...queryKeys.items.all, householdId, 'search-products', params] as const,
    productStats: (householdId: string, params?: ProductStatsParams) =>
      [...queryKeys.items.all, householdId, 'product-stats', params] as const,
  },
  admin: {
    all: ['admin'] as const,
    users: (params?: AdminUserQueryParams) => [...queryKeys.admin.all, 'users', params] as const,
    userDetail: (userId: string) => [...queryKeys.admin.all, 'users', userId] as const,
    metrics: () => [...queryKeys.admin.all, 'metrics'] as const,
    auditLogs: (params?: AuditLogQueryParams) =>
      [...queryKeys.admin.all, 'audit-logs', params] as const,
    auditLogDetail: (id: string) => [...queryKeys.admin.all, 'audit-logs', id] as const,
    products: (params?: AdminProductQueryParams) =>
      [...queryKeys.admin.all, 'products', params] as const,
    productDetail: (productId: string) => [...queryKeys.admin.all, 'products', productId] as const,
    productMetrics: () => [...queryKeys.admin.all, 'product-metrics'] as const,
  },
  announcements: {
    all: ['announcements'] as const,
    list: (params?: AnnouncementQueryParams) =>
      [...queryKeys.announcements.all, 'list', params] as const,
    detail: (id: string) => [...queryKeys.announcements.all, 'detail', id] as const,
    stats: (id: string) => [...queryKeys.announcements.all, 'stats', id] as const,
    active: (platform?: string) => [...queryKeys.announcements.all, 'active', platform] as const,
  },
  tours: {
    all: ['tours'] as const,
    list: () => [...queryKeys.tours.all, 'list'] as const,
    detail: (tourId: string) => [...queryKeys.tours.all, 'detail', tourId] as const,
  },
  viewPreferences: {
    all: ['viewPreferences'] as const,
    byPlatform: (platform: ViewPlatform) => [...queryKeys.viewPreferences.all, platform] as const,
  },
  shoppingLists: {
    all: ['shoppingLists'] as const,
    byHousehold: (householdId: string) => [...queryKeys.shoppingLists.all, householdId] as const,
    list: (householdId: string, params?: Record<string, unknown>) =>
      [...queryKeys.shoppingLists.all, householdId, 'list', params] as const,
    detail: (householdId: string, listId: string) =>
      [...queryKeys.shoppingLists.all, householdId, 'detail', listId] as const,
  },
  productGroups: {
    all: ['productGroups'] as const,
    byHousehold: (householdId: string) => [...queryKeys.productGroups.all, householdId] as const,
    list: (householdId: string, params?: Record<string, unknown>) =>
      [...queryKeys.productGroups.all, householdId, 'list', params] as const,
    detail: (householdId: string, groupId: string) =>
      [...queryKeys.productGroups.all, householdId, 'detail', groupId] as const,
    divergenceStats: (householdId: string) =>
      [...queryKeys.productGroups.all, householdId, 'divergence-stats'] as const,
    needsRestocking: (householdId: string) =>
      [...queryKeys.productGroups.all, householdId, 'needs-restocking'] as const,
  },
  shoppingTrips: {
    all: ['shoppingTrips'] as const,
    active: (householdId: string, listId: string) =>
      [...queryKeys.shoppingTrips.all, householdId, listId, 'active'] as const,
    detail: (householdId: string, listId: string, tripId: string) =>
      [...queryKeys.shoppingTrips.all, householdId, listId, 'detail', tripId] as const,
    items: (householdId: string, listId: string, tripId: string) =>
      [...queryKeys.shoppingTrips.all, householdId, listId, tripId, 'items'] as const,
  },
} as const;
